/**
 * Admin feature-flags API layer (STORY-008-009). Typed calls to the SHIPPED
 * STORY-008-005 CRUD router (`/admin/feature-flags*`). This story adds NO
 * backend routes. Types mirror the Pydantic schemas in
 * `apps/api/garden/admin/flags_schemas.py` (`FlagRule`, `FeatureFlagResponse`,
 * `FeatureFlagCreate`, `FeatureFlagUpdate`). `key` is immutable after create.
 */
import { apiClient } from "@/lib/api-client";

// ─────────────────────────── Types ─────────────────────────────────────────

/** One targeting rule (mirrors `FlagRule`): context[attribute] ∩ values. */
export interface FlagRule {
  attribute: string;
  values: string[];
}

/** One feature flag (mirrors `FeatureFlagResponse`). */
export interface AdminFeatureFlag {
  key: string;
  enabled: boolean;
  description: string | null;
  rules: FlagRule[];
  updated_at: string;
}

/** List envelope (mirrors `FeatureFlagListResponse`). */
export interface FeatureFlagListResponse {
  items: AdminFeatureFlag[];
}

/** POST body (mirrors `FeatureFlagCreate`). */
export interface FeatureFlagCreateBody {
  key: string;
  enabled: boolean;
  description: string | null;
  rules: FlagRule[];
}

/** PUT body (mirrors `FeatureFlagUpdate`; `key` is immutable). */
export interface FeatureFlagUpdateBody {
  enabled: boolean;
  description: string | null;
  rules: FlagRule[];
}

// ─────────────────────────── Query keys ────────────────────────────────────

export const flagKeys = {
  /** Invalidation root — every flag query lives under this prefix. */
  all: ["admin", "feature-flags"] as const,
  list: () => ["admin", "feature-flags", "list"] as const,
  detail: (key: string) => ["admin", "feature-flags", "detail", key] as const,
} as const;

// ─────────────────────────── Fetchers ──────────────────────────────────────

const BASE = "/admin/feature-flags";

export function listFlags(): Promise<FeatureFlagListResponse> {
  return apiClient.get<FeatureFlagListResponse>(BASE);
}

export function getFlag(key: string): Promise<AdminFeatureFlag> {
  return apiClient.get<AdminFeatureFlag>(`${BASE}/${encodeURIComponent(key)}`);
}

export function createFlag(
  body: FeatureFlagCreateBody,
): Promise<AdminFeatureFlag> {
  return apiClient.post<AdminFeatureFlag, FeatureFlagCreateBody>(BASE, body);
}

export function updateFlag(
  key: string,
  body: FeatureFlagUpdateBody,
): Promise<AdminFeatureFlag> {
  return apiClient.put<AdminFeatureFlag, FeatureFlagUpdateBody>(
    `${BASE}/${encodeURIComponent(key)}`,
    body,
  );
}

export function deleteFlag(key: string): Promise<void> {
  return apiClient.delete<void>(`${BASE}/${encodeURIComponent(key)}`);
}
